import capitalize from 'capitalize'
import dayjs from 'dayjs'
import startCase from 'lodash-es/startCase'
import { NextPageContext } from 'next'
import Head from 'next/head'
import SvgLogo from '#/assets/images/logos/app-icon.svg'
import Breadcrumb from '#/components/Breadcrumb'
import GeoAutocomplete from '#/components/GeoAutocomplete/advanced'
import Link from '#/components/Link'
import Pagination from '#/components/Pagination'
import { useGotoPlace } from '#/hooks/useGotoPlace'
import { getLinks, SitemapProcessed } from '#/services/sitemap'
import { wrapper } from '#/store/setup'
import cn from '#/utils/classnames'
import { commaNumber } from '#/utils/number'
import factsString from './factsString'
import styles from './styles.module.scss'

interface SitemapProps {
  components: string[]
  sitemap: SitemapProcessed
  page: number
  path: string
}

type MetricsType = {
  property_count: string | null,
  living_area: string | null,
  bedroom_count: number | null,
  bathroom_count: number | null,
  lot_size: number | null,
  year_built: number | null,
  estimate: string | null,
  price_per_square_foot: string | null
} | {
  living_area: string | null,
  bedroom_count: number | null,
  bathroom_count: number | null,
  lot_size: number | null,
  year_built: number | null,
  estimate: string | null,
  price_per_square_foot: string | null
} | null

export default function Sitemap({ components, sitemap, page, path }: SitemapProps) {
  const onChangePlace = useGotoPlace()
  const processedTitle = sitemap.title &&
    (sitemap.title?.charAt(0).toLowerCase() + sitemap.title?.slice(1))

  const metrics:MetricsType = sitemap.metrics ? (sitemap.metrics?.length === 8 ?
    {
      property_count: sitemap.metrics[0] ? commaNumber(sitemap.metrics[0]) : null,
      living_area: sitemap.metrics[1] ? `${commaNumber(sitemap.metrics[1])} square feet` : null,
      bedroom_count: sitemap.metrics[2] ? sitemap.metrics[2] : null,
      bathroom_count: sitemap.metrics[3] ? sitemap.metrics[3] : null,
      lot_size: sitemap.metrics[4] ? sitemap.metrics[4] : null,
      year_built: sitemap.metrics[5] ? sitemap.metrics[5] : null,
      estimate: sitemap.metrics[6] ? `$${commaNumber(sitemap.metrics[6])}` : null,
      price_per_square_foot: sitemap.metrics[7] ? `$${commaNumber(sitemap.metrics[7])}` : null
    } :
    {
      living_area: sitemap.metrics[1] ? `${commaNumber(sitemap.metrics[0])} square feet` : null,
      bedroom_count: sitemap.metrics[2] ? sitemap.metrics[1] : null,
      bathroom_count: sitemap.metrics[3] ? sitemap.metrics[2] : null,
      lot_size: sitemap.metrics[4] ? sitemap.metrics[3] : null,
      year_built: sitemap.metrics[5] ? sitemap.metrics[4] : null,
      estimate: sitemap.metrics[6] ? `$${commaNumber(sitemap.metrics[5])}` : null,
      price_per_square_foot: sitemap.metrics[7] ? `$${commaNumber(sitemap.metrics[6])}` : null
    }) :
    null

  const metricStrings = []

  for (const key in metrics) {
    if (metrics[key as keyof MetricsType] !== null) {
      if (key === 'property_count') {
        metricStrings.push(`${startCase(key)}: ${metrics[key as keyof MetricsType]}`)
      } else if (key === 'lot_size') {
        metricStrings.push(`Median ${startCase(key)}: ${metrics[key as keyof MetricsType]} acres`)
      } else {
        metricStrings.push(`Median ${startCase(key)}: ${metrics[key as keyof MetricsType]}`)
      }
    }
  }

  function formatBreadcrumbText(index: number, e: string): string {
    let result

    if (index <= 1) {
      // USA + State
      result = e.toUpperCase()
    } else {
      // Rest of the elements
      result = capitalize.words(e.replace('-', ' '))
    }

    // Append 'County' to counties
    if (index === 2) {
      result += ' County'
    }

    return result
  }

  const title = `List of ${sitemap.title === 'USA' ? `states in the ${sitemap.title}` : sitemap.title ?? 'Browse properties in ' + formatBreadcrumbText(components.length - 1, components[components.length - 1])} | TopHap`

  return (
    <div className={cn('container', styles['th-sitemap'])}>
      <Head>
        <title>{title}</title>
        <meta
          key='description'
          content={`This is a list of all ${sitemap.title === 'USA' ? `states in the ${sitemap.title}` : processedTitle ?? 'zones in this region'} indexed on TopHap as of ${dayjs(Date.now()).format('MMMM D, YYYY')}.${sitemap.metrics ? ` ${sitemap.name} ${factsString(sitemap.metrics)}` : ''}`}
          name='description'
        />
        {!sitemap.error ? (
          <>
            {(sitemap.pageCount || 0) > page ? <link href={`${path}?page=${page + 1}`} rel='next' /> : null}
            {(page > 1 && page <= (sitemap.pageCount || 0)) ? <link href={`${path}${page - 1 === 1 ? '' : `?page=${page - 1}`}`} rel='prev' /> : null}
            <link key='canonical' href={(typeof sitemap.pageCount !== undefined) ? `${path}${page === 1 ? '' : `?page=${page}`}` : path} rel='canonical' />
          </>
        ) :
          null}
      </Head>
      <Breadcrumb className='mt-6'>
        <SvgLogo className='mr-3' />
        {components.map((e, index) => (
          <Breadcrumb.Item key={e + index}>
            <Link
              href={`/sitemap/${components
                .slice(0, index + 1)
                .join('/')}`}
            >
              {formatBreadcrumbText(index, e)}
            </Link>
          </Breadcrumb.Item>
        ))}
      </Breadcrumb>
      <h1 className={styles['th-title']}>
        <span dangerouslySetInnerHTML={{ __html: sitemap.title === 'USA' ? 'States in the USA' : sitemap.title ?? 'Browse properties in ' + formatBreadcrumbText(components.length - 1, components[components.length - 1]) }} />
      </h1>
      <div className='mb-2'>
        {!sitemap.error && metrics && (
          <ul className='text-sm'>
            {metricStrings.map((string, index) => <li key={index}>{string}</li>)}
          </ul>
        )}
        <span />
      </div>
      {!sitemap.error && (
        <p>
          {`Browse properties by ${sitemap.title === 'USA' ? 'any state in the US' : processedTitle ?? 'zone'} on TopHap.`}
        </p>
      )}

      <GeoAutocomplete
        className={styles['th-geo-search']}
        multiple={false}
        placeholder='Address, Neighborhood, City'
        onChange={onChangePlace}
      />

      <div className={styles['th-links']}>
        <ul>
          {sitemap.items.map((e, index) => (
            <li key={index}>
              {e.url.startsWith('/homes/details') ? (
                <Link href={e.url}>
                  <span dangerouslySetInnerHTML={{ __html: e.title || '' }} className='font-bold' />
                  &nbsp;
                  <span className='text-xs'>{factsString(e.metrics)}</span>
                </Link>
              ) : (
                <Link href={`/sitemap/${components.join('/')}/${e.url.replace('/index.json', '')}`}>
                  <span className='font-bold'>{e.title}</span> <span className='text-xs'>{factsString(e.metrics)}</span>
                </Link>
              )}
            </li>
          ))}
        </ul>
      </div>
      {(sitemap.pageCount && sitemap.pageCount > 1) ? (
        <Pagination
          active={page}
          pageCount={sitemap.pageCount}
          path={path}
        />
      ) : null}
    </div>
  )
}

Sitemap.getInitialProps = wrapper.getInitialPageProps(() =>
  async ({ query, asPath }: NextPageContext) => {
    const page = isNaN(Number(query.page)) ? 1 : Number(query.page)
    const sitemap = await getLinks({
      page,
      url: asPath?.split('?')[0] as string
    })
    return {
      components: query.params,
      path: asPath?.split('?')[0],
      page,
      sitemap
    }
  })
